/**
 * Map activity feed CTAs from v2/rebuild paths to skipscreen-app routes.
 * Uses event metadata when present so links open the specific question, request, or prompt.
 */
export function resolveActivityCtaPath(
  category: string,
  ctaPath: string,
  ctaLabel: string,
  metadata?: Record<string, unknown> | null
): string {
  const path = (ctaPath || '').trim();
  const label = (ctaLabel || '').trim().toLowerCase();
  const cat = (category || '').trim().toLowerCase();
  const md = metadata ?? {};

  const chatRequestId = md.chat_request_id ?? md.chatRequestId;
  const accessRequestId = md.access_request_id ?? md.accessRequestId;
  const promptId = md.prompt_id ?? md.promptId;

  if (cat === 'recruiter_chat_request') {
    if (label.includes('question') || path.includes('/messages/questions')) {
      if (chatRequestId != null) {
        return `/messages/questions?question=${encodeURIComponent(String(chatRequestId))}`;
      }
      return '/messages/questions';
    }
    if (chatRequestId != null) {
      return `/messages/requests?request=${encodeURIComponent(String(chatRequestId))}`;
    }
    return '/messages/requests';
  }

  if (cat === 'recruiter_access_request' && accessRequestId != null) {
    const email = md.recruiter_email ?? md.recruiterEmail;
    const requestedField = md.requested_field ?? md.requestedField;
    const qs = new URLSearchParams({
      accessRequest: String(accessRequestId),
    });
    if (email) qs.set('recruiterEmail', String(email));
    if (requestedField) qs.set('requestedField', String(requestedField));
    return `/dashboard?${qs.toString()}`;
  }

  if ((cat === 'user_prompt_added' || cat === 'recruiter_prompt_video_watch') && promptId != null) {
    return `/prompts?prompt=${encodeURIComponent(String(promptId))}`;
  }

  // Honor query strings already stored on cta_path (newer events).
  if (path.includes('?')) {
    const base = path.split('?')[0];
    const rewritten = rewriteLegacyPath(base);
    const qs = path.slice(path.indexOf('?'));
    return `${rewritten}${qs}`;
  }

  const byCategory: Record<string, string> = {
    recruiter_meeting_request: '/calendar',
    recruiter_meeting_approved: '/calendar',
    recruiter_meeting_denied: '/calendar',
    recruiter_meeting_rescheduled: '/calendar',
    recruiter_message_received: '/messages',
    recruiter_access_request: '/dashboard',
    recruiter_access_approved: '/notifications',
    recruiter_access_denied: '/notifications',
    recruiter_saved_profile: '/analytics',
    recruiter_viewed_profile: '/analytics',
    recruiter_liked_section: '/analytics',
    recruiter_expanded_section: '/analytics',
    recruiter_profile_dwell: '/analytics',
    recruiter_prompt_video_watch: '/prompts',
    user_prompt_added: '/prompts',
    user_prompt_updated: '/prompts',
    user_prompt_deleted: '/prompts',
    user_skills_updated: '/user-profile',
    user_profile_updated: '/user-profile',
    user_password_changed: '/settings',
    user_email_verified: '/dashboard',
    user_google_calendar_connected: '/calendar',
    user_google_calendar_disconnected: '/calendar',
    user_reference_added: '/dashboard/references',
    user_reference_confirmed: '/dashboard/references',
    user_availability_added: '/calendar',
    user_availability_removed: '/calendar',
    system_onboarding_complete: '/dashboard',
    system_profile_activated: '/user-profile',
    system_profile_deactivated: '/user-profile',
    user_pro_upgraded: '/settings',
  };
  if (byCategory[cat]) return byCategory[cat];

  const rewritten = rewriteLegacyPath(path);
  if (rewritten !== path) return rewritten;

  if (label.includes('question')) {
    return chatRequestId != null
      ? `/messages/questions?question=${encodeURIComponent(String(chatRequestId))}`
      : '/messages/questions';
  }
  if (label.includes('prompt')) {
    return promptId != null
      ? `/prompts?prompt=${encodeURIComponent(String(promptId))}`
      : '/prompts';
  }
  if (label.includes('profile') || label.includes('skills')) return '/user-profile';
  if (label.includes('calendar') || label.includes('interview')) return '/calendar';
  if (label.includes('access')) {
    if (accessRequestId != null) {
      const email = md.recruiter_email ?? md.recruiterEmail;
      const requestedField = md.requested_field ?? md.requestedField;
      const qs = new URLSearchParams({
        accessRequest: String(accessRequestId),
      });
      if (email) qs.set('recruiterEmail', String(email));
      if (requestedField) qs.set('requestedField', String(requestedField));
      return `/dashboard?${qs.toString()}`;
    }
    return '/dashboard';
  }
  if (label.includes('request')) return '/messages/requests';
  if (label.includes('analytics')) return '/analytics';
  if (label.includes('dashboard')) return '/dashboard';

  if (!path) return '/dashboard';
  return path.startsWith('/') ? path : `/${path}`;
}

function rewriteLegacyPath(path: string): string {
  const pathRewrites: Record<string, string> = {
    '/messageshub': '/messages/questions',
    '/messageshub/': '/messages/questions',
    '/chat-requests': '/messages/requests',
    '/profile': '/user-profile',
    '/profile#profile-skills': '/user-profile',
    '/profile#profile-summary': '/user-profile',
    '/profile#profile-portfolio': '/create-portfolio',
    '/create-prompt': '/prompts',
  };
  if (pathRewrites[path]) return pathRewrites[path];
  if (path.startsWith('/profile#')) return '/user-profile';
  return path;
}
